import { getErrorCode } from "@onadssuper/api-client";
import { WEEKDAYS, WEEKDAY_LABELS, type Visibility } from "@onadssuper/validators";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Switch,
} from "../../../components/ui";
import { useCan, useCurrentWorkspace } from "../../../features/workspace/workspace-context";
import { useTRPC } from "../../../lib/trpc";

export const Route = createFileRoute("/w/$slug/settings")({ component: Settings });

function Settings() {
  const workspace = useCurrentWorkspace();
  const canEdit = useCan("workspace.update_policy");
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const me = useQuery(trpc.me.get.queryOptions());
  const ownMfa = Boolean(me.data?.user.twoFactorEnabled);

  const update = useMutation(
    trpc.workspaces.updatePolicy.mutationOptions({
      meta: { silent: true },
      onSuccess: async (result) => {
        toast.success(
          result.requireMfa
            ? "Two-factor authentication is now required"
            : "Two-factor authentication is now optional",
        );
        await queryClient.invalidateQueries({ queryKey: trpc.workspaces.current.queryKey() });
      },
      onError: async (error) => {
        if (getErrorCode(error) === "CONFLICT") {
          toast.error("Someone else changed this setting. Showing the latest value.");
          await queryClient.invalidateQueries({ queryKey: trpc.workspaces.current.queryKey() });
        } else {
          toast.error(error.message);
        }
      },
    }),
  );

  if (!canEdit) {
    return <EmptyState title="Settings are managed by agency owners and admins" />;
  }

  // Turning the policy on without MFA yourself would lock you out immediately.
  const blockedForSelf = !workspace.requireMfa && !ownMfa;

  return (
    <>
      <PageHeader
        title="Settings"
        description="Policies that apply to everyone in this workspace."
      />
      <Card>
        <CardHeader title="Security" />
        <CardBody>
          <div className="flex items-start justify-between gap-6">
            <div>
              <p id="require-mfa-label" className="text-sm font-medium">
                Require two-factor authentication
              </p>
              <p id="require-mfa-desc" className="mt-1 max-w-prose text-sm text-muted">
                Members without an authenticator app set up can't open this workspace until they add
                one. Google and Microsoft sign-in can't be used by accounts with two-factor enabled.
              </p>
              {blockedForSelf && (
                <p className="mt-2 text-sm text-warning">
                  Set up two-factor authentication on{" "}
                  <Link to="/account/security" className="underline">
                    your account
                  </Link>{" "}
                  first, or you'd lock yourself out.
                </p>
              )}
            </div>
            <Switch
              aria-labelledby="require-mfa-label"
              aria-describedby="require-mfa-desc"
              checked={workspace.requireMfa}
              disabled={update.isPending || blockedForSelf}
              onCheckedChange={(checked) =>
                update.mutate({ requireMfa: checked, expectedVersion: workspace.version })
              }
            />
          </div>
        </CardBody>
      </Card>
      <div className="mt-4">
        <WorkingRules />
      </div>
    </>
  );
}

/** Rules 14, 15: what forms preselect, and which days the team works. */
function WorkingRules() {
  const workspace = useCurrentWorkspace();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [visibility, setVisibility] = useState<Visibility>(workspace.defaultVisibility);
  const [days, setDays] = useState<number[]>(workspace.workingDays);
  const save = useMutation(
    trpc.workspaces.updatePolicy.mutationOptions({
      meta: { silent: true },
      onSuccess: async () => {
        toast.success("Working rules saved");
        await queryClient.invalidateQueries({ queryKey: trpc.workspaces.current.queryKey() });
      },
      onError: async (error) => {
        toast.error(
          getErrorCode(error) === "CONFLICT"
            ? "Someone else changed these rules. Showing the latest values."
            : error.message,
        );
        await queryClient.invalidateQueries({ queryKey: trpc.workspaces.current.queryKey() });
      },
    }),
  );
  return (
    <section aria-label="Working rules">
      <Card>
        <CardHeader title="Working rules" />
        <CardBody className="flex max-w-xl flex-col gap-4">
          <Field label="New items start as">
            <Select
              value={visibility}
              onValueChange={(v) => setVisibility(v === "agency_only" ? v : "shared")}
              options={[
                { value: "shared", label: "Shared with the client" },
                { value: "agency_only", label: "Agency only" },
              ]}
            />
          </Field>
          <fieldset>
            <legend className="text-sm font-medium">Working days</legend>
            <div className="mt-2 flex flex-wrap gap-3 text-sm">
              {WEEKDAYS.map((day, index) => (
                <label key={day} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={days.includes(day)}
                    onChange={(e) =>
                      setDays(e.target.checked ? [...days, day] : days.filter((d) => d !== day))
                    }
                  />
                  {WEEKDAY_LABELS[index]}
                </label>
              ))}
            </div>
          </fieldset>
          <div>
            <Button
              loading={save.isPending}
              disabled={days.length === 0}
              onClick={() =>
                save.mutate({
                  defaultVisibility: visibility,
                  workingDays: days,
                  expectedVersion: workspace.version,
                })
              }
            >
              Save rules
            </Button>
          </div>
        </CardBody>
      </Card>
    </section>
  );
}
